/**
 * Journey calorie calculation API + local adapter.
 */
import { apiPost } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { computeJourneyUserNeeds, normalizeJourneyLocalGoal } from './localSuggest';

export interface JourneyCalorieForm {
  type: string;
  gender: string;
  birth_date: string;
  age: number;
  height: number;
  weight: number;
  activity_level: string;
  activity: string;
  /** Wizard activity option id when available (`sedentary`, `lightly_active`, …). */
  activity_id?: string;
}

export interface JourneyCalorieResult {
  calories: number | null;
  message?: string;
  /** Present for local calc — full needs for result ranking. */
  needs?: ReturnType<typeof computeJourneyUserNeeds>;
}

function parseJourneyCalorieResponse(response: unknown): JourneyCalorieResult {
  const res = response as Record<string, unknown> | null | undefined;
  let inner: unknown = res?.data != null && typeof res.data === 'object' ? res.data : response;
  if (inner != null && typeof inner === 'object') {
    const inner_record = inner as Record<string, unknown>;
    if (inner_record.data != null && typeof inner_record.data === 'object') {
      const nested = inner_record.data as Record<string, unknown>;
      if (nested.active_metabolic_rate != null || nested.calories != null || nested.bmr != null) {
        inner = nested;
      }
    }
  }
  const inner_record = inner as Record<string, unknown> | null | undefined;
  const calories_value =
    inner_record?.active_metabolic_rate ??
    inner_record?.calories ??
    inner_record?.bmr ??
    inner_record?.daily_calories;
  const calories =
    calories_value != null && Number(calories_value) > 0 ? Number(calories_value) : null;
  const message = (res?.message ?? inner_record?.message) as string | undefined;
  return { calories, message };
}

export async function calculateJourneyCalories(form: JourneyCalorieForm): Promise<JourneyCalorieResult> {
  const response = await apiPost<unknown>(endpoints.calculate_calories, form);
  return parseJourneyCalorieResponse(response);
}

/**
 * In-app journey calorie target (`is_journey_by_api: false`).
 * Uses Mifflin–St Jeor + activity + goal factors from `localSuggest.ts`.
 */
export async function calculateJourneyCaloriesLocal(form: JourneyCalorieForm): Promise<JourneyCalorieResult> {
  const needs = computeJourneyUserNeeds({
    goal: normalizeJourneyLocalGoal(form.type),
    gender: form.gender,
    age: form.age,
    height_cm: form.height,
    weight_kg: form.weight,
    activity: form.activity_id || form.activity || form.activity_level,
  });
  if (!(needs.target_calories > 0)) {
    return { calories: null, message: 'Could not calculate local calorie target.', needs };
  }
  return { calories: needs.target_calories, needs };
}
